import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions as MuiDialogActions,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import api from "../../../API/axiosInstance";
import { API_ENDPOINTS } from "config/api";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const AdminUploadedCurrentStudentsDialog = ({ open, onClose }) => {
  const gridRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSingleId, setDeleteSingleId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchData();
    } else {
      setSelectedIds(new Set());
      setMessage({ type: "", text: "" });
      // Clear grid selection when dialog closes
      if (gridRef.current?.api) {
        gridRef.current.api.deselectAll();
      }
    }
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    try {
      const url = API_ENDPOINTS.STUDENT_REQUEST.GET_ALL_UPLOADED_STUDENTS(true);
      const response = await api.get(url);
      const dataArray = response.data || [];
      
      // Debug: Log first record to check property names
      if (dataArray.length > 0) {
        console.log("Sample uploaded current students data:", dataArray[0]);
      }
      
      // Transform data for AG Grid
      const transformedData = dataArray.map((item) => {
        const uploadId = item.uploadId || item.UploadId;
        const formatDate = (dateValue) => {
          if (!dateValue) return "-";
          try {
            return new Date(dateValue).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          } catch {
            return "-";
          }
        };

        return {
          uploadId,
          id: item.id || item.Id || item.erpStudentId || item.ErpStudentId || "-",
          rollNumber: item.rollNumber || item.RollNumber || "-",
          regNumber: item.regNumber || item.RegNumber || "-",
          name: item.name || item.Name || item.fullName || item.FullName || `${item.firstName || item.FirstName || ''} ${item.lastName || item.LastName || ''}`.trim() || "-",
          gender: item.gender || item.Gender || "-",
          dateOfBirth: formatDate(item.dateOfBirth || item.DateOfBirth),
          maritalStatus: item.maritalStatus || item.MaritalStatus || "-",
          batchYear: item.batchYear || item.BatchYear || "-",
          collegeStudied: item.collegeStudied || item.CollegeStudied || "-",
          courseStudied: item.courseStudied || item.CourseStudied || "-",
          branch: item.branch || item.Branch || "-",
          contactNumber: item.contactNumber || item.ContactNumber || "-",
          whatsappNumber: item.whatsappNumber || item.WhatsappNumber || item.whatsAppNumber || item.WhatsAppNumber || "-",
          emailID: item.emailID || item.EmailID || item.email || item.Email || "-",
          presentStatus: item.presentStatus || item.PresentStatus || "-",
          forOthers: item.forOthers || item.ForOthers || "-",
          organization: item.organization || item.Organization || "-",
          designation: item.designation || item.Designation || "-",
          location: item.location || item.Location || "-",
          availability: item.availability || item.Availability || "-",
          feedback: item.feedback || item.Feedback || "-",
          nativePlace: item.nativePlace || item.NativePlace || "-",
          domain: item.domain || item.Domain || "-",
          category: item.category || item.Category || "-",
          firstName: item.firstName || item.FirstName || "-",
          lastName: item.lastName || item.LastName || "-",
          email: item.email || item.Email || item.emailID || item.EmailID || "-",
          mobile: item.mobile || item.Mobile || item.contactNumber || item.ContactNumber || "-",
          uploadedBy: item.uploadedBy || item.UploadedBy || item.uploadedByName || item.UploadedByName || "-",
          uploadedDate: formatDate(item.uploadedDate || item.UploadedDate),
          isActive: item.isActive || item.IsActive ? "Yes" : "No",
        };
      });
      
      setRowData(transformedData);
    } catch (error) {
      console.error("Error fetching uploaded current students data:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to fetch uploaded current students data.",
      });
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const onSelectionChanged = useCallback(() => {
    if (gridRef.current?.api) {
      const selectedRows = gridRef.current.api.getSelectedRows();
      const selectedUploadIds = new Set(selectedRows.map((row) => row.uploadId));
      setSelectedIds(selectedUploadIds);
    }
  }, []);

  const onGridReady = useCallback((params) => {
    params.api.sizeColumnsToFit();
  }, []);

  const handleDeleteSingle = useCallback((uploadId) => {
    setDeleteSingleId(uploadId);
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteBulk = () => {
    if (selectedIds.size === 0) {
      setMessage({ type: "warning", text: "Please select at least one record to delete." });
      return;
    }
    setDeleteSingleId(null);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setMessage({ type: "", text: "" });
    setDeleteConfirmOpen(false);

    try {
      const idsToDelete = deleteSingleId ? [deleteSingleId] : Array.from(selectedIds);

      const response = await api.post(API_ENDPOINTS.STUDENT_REQUEST.DELETE_UPLOADED_STUDENTS, {
        uploadIds: idsToDelete,
        deletedBy: 1, // TODO: Get from auth context
      });

      setMessage({
        type: "success",
        text: response.data?.message || `${response.data?.deletedCount || 0} record(s) deleted successfully!`,
      });

      // Refresh data
      await fetchData();
      setSelectedIds(new Set());
      setDeleteSingleId(null);
      // Clear grid selection
      if (gridRef.current?.api) {
        gridRef.current.api.deselectAll();
      }
    } catch (error) {
      console.error("Error deleting records:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to delete records.",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Delete action cell renderer
  const deleteActionCellRenderer = useCallback((params) => {
    const uploadId = params.data?.uploadId;
    if (!uploadId) return null;

    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSingle(uploadId);
            }}
            disabled={deleting}
            sx={{ 
              padding: "4px",
              "& .MuiSvgIcon-root": {
                fontSize: "1rem"
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, [deleting, handleDeleteSingle]);

  // Text cell renderer for long text with ellipsis
  const textCellRenderer = useCallback((params) => {
    const value = params.value ?? "-";
    return (
      <span 
        title={value} 
        style={{ 
          display: "inline-block", 
          maxWidth: "100%", 
          overflow: "hidden", 
          textOverflow: "ellipsis", 
          whiteSpace: "nowrap",
          fontSize: "0.75rem" // Smaller font size
        }}
      >
        {value}
      </span>
    );
  }, []);

  // Column definitions for AG Grid
  const columnDefs = useMemo(() => [
    {
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      resizable: false,
      sortable: false,
      filter: false,
      pinned: "left",
      suppressSizeToFit: true,
      suppressMenu: true,
      cellStyle: { 
        textAlign: "center", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        fontSize: "0.75rem"
      },
    },
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
      }
        return rowIdx + 1;
      },
      width: 55,
      minWidth: 50,
      maxWidth: 60,
      resizable: false,
      sortable: false,
      filter: false,
      pinned: "left",
      suppressSizeToFit: true,
      suppressMenu: true,
      cellStyle: { textAlign: "center", fontSize: "0.75rem" },
    },
    {
      field: "id",
      headerName: "Id",
      sortable: true,
      filter: "agNumberColumnFilter",
      floatingFilter: true,
      width: 80,
      minWidth: 70,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
      },
    },
    {
      field: "rollNumber",
      headerName: "Roll Number",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 100,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "regNumber",
      headerName: "Registration Number",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 130,
      minWidth: 110,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "name",
      headerName: "Name",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1.5,
      minWidth: 120,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "gender",
      headerName: "Gender",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 80,
      minWidth: 70,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "dateOfBirth",
      headerName: "Date of Birth",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 100,
      minWidth: 90,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "maritalStatus",
      headerName: "Marital Status",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 100,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "batchYear",
      headerName: "Batch Year",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 85,
      minWidth: 75,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      field: "collegeStudied",
      headerName: "College Studied",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1.5,
      minWidth: 120,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "courseStudied",
      headerName: "Course Studied",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1.5,
      minWidth: 120,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "branch",
      headerName: "Branch",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "contactNumber",
      headerName: "Contact Number",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 110,
      minWidth: 100,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "whatsappNumber",
      headerName: "WhatsApp Number",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 110,
      minWidth: 100,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "emailID",
      headerName: "Email ID",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1.5,
      minWidth: 130,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "presentStatus",
      headerName: "Present Status",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "forOthers",
      headerName: "For Others",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 100,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "organization",
      headerName: "Organization",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1.5,
      minWidth: 120,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "designation",
      headerName: "Designation",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "location",
      headerName: "Location",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "availability",
      headerName: "Availability",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "feedback",
      headerName: "Feedback",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 2,
      minWidth: 120,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "nativePlace",
      headerName: "Native Place",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1,
      minWidth: 100,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "domain",
      headerName: "Domain",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 100,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "category",
      headerName: "Category",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 100,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "firstName",
      headerName: "First Name",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "lastName",
      headerName: "Last Name",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "email",
      headerName: "Email",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      flex: 1.5,
      minWidth: 130,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "mobile",
      headerName: "Mobile",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 100,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "uploadedBy",
      headerName: "Uploaded By",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 100,
      minWidth: 90,
      cellRenderer: textCellRenderer,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "uploadedDate",
      headerName: "Uploaded Date",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 110,
      minWidth: 100,
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'contains',
      },
    },
    {
      field: "isActive",
      headerName: "Is Active",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      width: 80,
      minWidth: 70,
      cellStyle: (params) => {
        return {
          fontSize: "0.75rem",
          ...(params.value === "Yes" 
            ? { color: "#4caf50", fontWeight: "bold" }
            : { color: "#f44336", fontWeight: "bold" })
        };
      },
      filterParams: {
        buttons: ['reset', 'apply'],
        closeOnApply: true,
        defaultOption: 'equals',
      },
    },
    {
      headerName: "Actions",
      field: "action",
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      resizable: false,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      suppressMenu: true,
      cellRenderer: deleteActionCellRenderer,
      cellStyle: { 
        textAlign: "center", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        fontSize: "0.75rem"
      },
      pinned: "right",
    },
  ], [textCellRenderer, deleteActionCellRenderer]);

  // Default column definition
  const defaultColDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: true, // Show filter input in header
    flex: 1,
    cellStyle: { fontSize: "0.75rem" }, // Smaller font size for all cells
    headerClass: "ag-header-cell-small", // Custom class for header
    filterParams: {
      buttons: ['reset', 'apply'],
      closeOnApply: true,
    },
  }), []);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" fullScreen>
      <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}>
        <IconButton onClick={onClose} color="error">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <MDTypography variant="h4" fontWeight="bold">
              Uploaded Current Students Data
            </MDTypography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Chip
              label={`${selectedIds.size} selected`}
              color="primary"
              variant={selectedIds.size > 0 ? "filled" : "outlined"}
            />
            <MDButton
              variant="gradient"
              color="error"
              startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
              onClick={handleDeleteBulk}
              disabled={selectedIds.size === 0 || deleting}
            >
              {deleting ? "Deleting..." : `Delete Selected (${selectedIds.size})`}
            </MDButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
            {message.text}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : rowData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <MDTypography variant="h6" color="text.secondary">
              No uploaded current students data found.
            </MDTypography>
          </Box>
        ) : (
          <>
            <style>{`
              .ag-theme-alpine .ag-header-cell,
              .ag-theme-alpine .ag-header-group-cell {
                font-size: 0.75rem !important;
                font-weight: 600;
                padding: 4px 8px !important;
              }
              .ag-theme-alpine .ag-cell {
                font-size: 0.75rem !important;
                padding: 4px 8px !important;
                line-height: 1.4;
              }
              .ag-theme-alpine .ag-row {
                min-height: 28px !important;
              }
              .ag-theme-alpine .ag-header-row {
                min-height: 32px !important;
              }
              .ag-theme-alpine .ag-header-row-filter {
                min-height: 22px !important;
              }
              .ag-theme-alpine .ag-paging-panel {
                font-size: 0.75rem !important;
                padding: 4px 8px !important;
              }
              .ag-theme-alpine .ag-filter-toolpanel-header,
              .ag-theme-alpine .ag-filter-toolpanel-search,
              .ag-theme-alpine .ag-filter-toolpanel-group-title {
                font-size: 0.75rem !important;
              }
              .ag-theme-alpine .ag-floating-filter-input,
              .ag-theme-alpine .ag-floating-filter-body {
                font-size: 0.65rem !important;
                height: 20px !important;
                min-height: 20px !important;
              }
              .ag-theme-alpine .ag-floating-filter-input input {
                font-size: 0.65rem !important;
                padding: 1px 3px !important;
                height: 20px !important;
                line-height: 20px !important;
              }
              .ag-theme-alpine .ag-floating-filter-wrapper {
                height: 20px !important;
                min-height: 20px !important;
              }
              .ag-theme-alpine .ag-header-cell-filtered {
                background-color: #e3f2fd !important;
              }
            `}</style>
            <div className="ag-theme-alpine" style={{ height: "75vh", width: "100%" }}>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowSelection="multiple"
                animateRows={true}
                pagination={true}
                paginationPageSize={30}
                onGridReady={onGridReady}
                onSelectionChanged={onSelectionChanged}
                suppressRowClickSelection={true}
                enableRangeSelection={false}
                rowHeight={28}
                headerHeight={32}
                floatingFiltersHeight={22}
                getRowId={(params) => params.data.uploadId}
                suppressFieldDotNotation={true}
                suppressColumnVirtualisation={false}
                suppressMenuHide={false}
              />
            </div>
          </>
        )}
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text.secondary">
            {deleteSingleId
              ? "Are you sure you want to delete this record? This action cannot be undone."
              : `Are you sure you want to delete ${selectedIds.size} selected record(s)? This action cannot be undone.`}
          </MDTypography>
        </DialogContent>
        <MuiDialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </MuiDialogActions>
      </Dialog>
    </Dialog>
  );
};

AdminUploadedCurrentStudentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminUploadedCurrentStudentsDialog;
